import React, { useState } from "react";
import { Helmet } from "react-helmet-async";

/* ============================================================================
   ELVIS ALOHA ARCHIVE — PREMIUM AUCTION PRESENTATION
   
   NOTE: This page is DEPRECATED and should not be indexed.
   It contains auction/investment language that is prohibited on the public site.
   Maintained only as internal backup - NOT linked from main showcase.
   
   Designed for serious collectors, institutions, museums, and investors.
   Focus: Rarity, Provenance, Completeness, Investment Significance
   
   NOT a fan site. NOT a general museum page.
   This is a professional buyer presentation.
   ============================================================================ */

// High-quality collection imagery
const IMAGES = {
  heroCollection: "/assets/collection/real-graceland-costume-tickets-2.jpg",
  globePortrait: "/assets/collection/real-proof-globe-portrait-1.jpg",
  documents: "/assets/collection/real-graceland-documents-1.jpg",
  artworkProofs: "/assets/collection/real-proof-artwork-grid.jpg",
  ticketDetail: "/assets/collection/real-graceland-costume-tickets-1.jpg",
  gracelandDisplay: "/assets/collection/real-graceland-framed-tickets-1.jpg"
};

/* ============================================================================
   SMOOTH SCROLL
   ============================================================================ */
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }
};

/* ============================================================================
   PREMIUM NAVIGATION
   ============================================================================ */
const PremiumNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'highlights', label: 'Highlights' },
    { id: 'provenance', label: 'Provenance' },
    { id: 'significance', label: 'Significance' },
    { id: 'inquiry', label: 'Inquire' }
  ];

  return (
    <>
      <style>{`
        .auction-nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 100;
          transition: all 0.3s ease;
          background: ${scrolled ? 'rgba(10, 10, 10, 0.98)' : 'transparent'};
          border-bottom: ${scrolled ? '1px solid rgba(255,255,255,0.1)' : 'none'};
        }
        .auction-nav-link {
          padding: 8px 20px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.7);
          background: none;
          border: none;
          cursor: pointer;
          transition: color 0.2s;
        }
        .auction-nav-link:hover { color: #C9A962; }
        @media (max-width: 768px) {
          .auction-desktop-nav { display: none !important; }
          .auction-mobile-toggle { display: flex !important; }
        }
        @media (min-width: 769px) {
          .auction-desktop-nav { display: flex !important; }
          .auction-mobile-toggle { display: none !important; }
        }
      `}</style>
      
      <nav className="auction-nav" data-testid="auction-nav">
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '20px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
             style={{ textDecoration: 'none' }}>
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '18px',
              fontWeight: 600,
              color: 'white',
              letterSpacing: '0.05em'
            }}>
              ELVIS ALOHA ARCHIVE
            </span>
          </a>

          <div className="auction-desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {navItems.map(item => (
              <button key={item.id} className="auction-nav-link" onClick={() => scrollToSection(item.id)}>
                {item.label}
              </button>
            ))}
          </div>

          <button className="auction-mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)}
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
              {mobileOpen ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></> 
                         : <><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/></>}
            </svg>
          </button>
        </div>

        {mobileOpen && (
          <div style={{ background: 'rgba(10,10,10,0.98)', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '16px' }}>
            {navItems.map(item => (
              <button key={item.id} onClick={() => { scrollToSection(item.id); setMobileOpen(false); }}
                style={{ display: 'block', width: '100%', padding: '16px', textAlign: 'left', background: 'none', border: 'none',
                  fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', cursor: 'pointer' }}>
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>
    </>
  );
};

/* ============================================================================
   HERO SECTION - Auction Presentation
   ============================================================================ */
const HeroSection = () => (
  <section id="top" style={{
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: '#0a0a0a',
    overflow: 'hidden'
  }} data-testid="hero-section">
    {/* Background image with overlay */}
    <div style={{
      position: 'absolute',
      inset: 0,
      backgroundImage: `url(${IMAGES.heroCollection})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      opacity: 0.15
    }} />
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(180deg, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.7) 50%, rgba(10,10,10,0.95) 100%)'
    }} />

    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '140px 32px 100px',
      textAlign: 'center'
    }}>
      <p style={{
        marginBottom: '24px',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: '12px',
        fontWeight: 600,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: '#C9A962'
      }}>
        A Complete Historical Archive Available for Acquisition
      </p>

      <h1 style={{
        margin: '0 0 32px',
        fontFamily: "'Cormorant Garamond', serif",
        fontSize: 'clamp(36px, 8vw, 72px)',
        fontWeight: 400,
        lineHeight: 1.1,
        color: 'white',
        letterSpacing: '-0.01em'
      }}>
        The Elvis Presley<br />
        <span style={{ fontStyle: 'italic' }}>Aloha from Hawaii</span><br />
        Collection
      </h1>

      <p style={{
        maxWidth: '700px',
        margin: '0 auto 48px',
        fontFamily: "'Source Serif 4', serif",
        fontSize: 'clamp(16px, 2.5vw, 20px)',
        lineHeight: 1.7,
        color: 'rgba(255,255,255,0.75)'
      }}>
        The definitive archive of original tickets, production materials, printing proofs, 
        photographs, and documents from the world's first global satellite concert broadcast. 
        January 14, 1973.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '48px', marginBottom: '48px' }}>
        {[
          { value: '50+', label: 'Original Tickets' },
          { value: '1973', label: 'Satellite Broadcast' },
          { value: 'Complete', label: 'Production Archive' }
        ].map((stat, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <p style={{ margin: 0, fontFamily: "'Cormorant Garamond', serif", fontSize: '36px', fontWeight: 600, color: '#C9A962' }}>
              {stat.value}
            </p>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      <button onClick={() => scrollToSection('inquiry')} style={{
        padding: '18px 48px',
        background: 'transparent',
        border: '1px solid #C9A962',
        color: '#C9A962',
        fontFamily: "'Source Sans 3', sans-serif",
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => { e.target.style.background = '#C9A962'; e.target.style.color = '#0a0a0a'; }}
      onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#C9A962'; }}
      data-testid="hero-cta">
        Request Information
      </button>
    </div>

    <button onClick={() => scrollToSection('overview')} style={{
      position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)',
      background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)'
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="m6 9 6 6 6-6"/>
      </svg>
    </button>
  </section>
);

/* ============================================================================
   COLLECTION OVERVIEW
   ============================================================================ */
const OverviewSection = () => (
  <section id="overview" style={{ padding: '120px 32px', background: 'white' }} data-testid="overview-section">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '64px' }}>
        <p style={{ marginBottom: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#B8860B' }}>
          Collection Overview
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 400, color: '#1a1a1a', lineHeight: 1.2 }}>
          A Complete Production Archive,<br />Not a Group of Unrelated Items
        </h2>
        <p style={{ maxWidth: '700px', margin: '0 auto', fontFamily: "'Source Serif 4', serif", fontSize: '17px', lineHeight: 1.8, color: '#555' }}>
          This collection represents the complete documented record of the Aloha from Hawaii concert ticket 
          production—from original Globe Tickets printing proofs through finished tickets, correspondence, 
          photographs, and supporting materials. It is an interconnected historical archive that would be 
          extremely difficult, if not impossible, to reassemble.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '32px' }}>
        {[
          {
            title: 'Original Concert Tickets',
            desc: 'More than 50 original tickets from January 14, 1973, including multiple price points, seating sections, and design variations. Many retain original condition with minimal handling.',
            count: '50+'
          },
          {
            title: 'Production Proofs & Materials',
            desc: 'Original Globe Tickets printing proofs, color tests, progressive proofs, and production materials documenting the complete ticket creation process.',
            count: 'Archive'
          },
          {
            title: 'Photographs & Documents',
            desc: 'Original photographs, correspondence, contracts, planning documents, and contemporaneous newspaper coverage from 1972-1973.',
            count: 'Complete'
          },
          {
            title: 'Printing Artifacts',
            desc: 'One-of-a-kind production materials including printer\'s proofs, color separation tests, and materials that document the physical creation of these historic tickets.',
            count: 'Unique'
          }
        ].map((item, idx) => (
          <div key={idx} style={{ padding: '32px', background: '#fafafa', border: '1px solid #e8e8e8' }}>
            <p style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#B8860B' }}>
              {item.count}
            </p>
            <h3 style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>
              {item.title}
            </h3>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#666' }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   HISTORICAL SIGNIFICANCE
   ============================================================================ */
const HistoricalSection = () => (
  <section style={{ padding: '100px 32px', background: '#0a0a0a', color: 'white' }}>
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: '64px', alignItems: 'center' }}>
        <div>
          <p style={{ marginBottom: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#C9A962' }}>
            Historical Context
          </p>
          <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 5vw, 38px)', fontWeight: 400, lineHeight: 1.2 }}>
            The First Global Satellite Concert Broadcast
          </h2>
          <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: '16px', lineHeight: 1.8, color: 'rgba(255,255,255,0.8)' }}>
            <p style={{ marginBottom: '20px' }}>
              On January 14, 1973, Elvis Presley performed at the Honolulu International Center Arena 
              in what became the first entertainment program transmitted globally via satellite. The 
              <em> Aloha from Hawaii Via Satellite</em> concert was broadcast live to participating 
              markets across Asia and Oceania.
            </p>
            <p style={{ marginBottom: '20px' }}>
              The concert was a benefit for the Kui Lee Cancer Fund. On February 4, 1973, Elvis 
              presented a check for $77,000 to the charity—documented in photographs included in this archive.
            </p>
            <p>
              The United States broadcast followed on April 4, 1973, on NBC. The program drew an 
              estimated global audience that established new records for television viewership.
            </p>
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          <img src={IMAGES.gracelandDisplay} alt="The Aloha from Hawaii ticket collection" 
            style={{ width: '100%', height: 'auto', display: 'block' }} />
          <p style={{ marginTop: '12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
            Collection documented at Graceland, September 14, 2022
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
   COLLECTION HIGHLIGHTS - Featured Items
   ============================================================================ */
const HighlightsSection = () => (
  <section id="highlights" style={{ padding: '120px 32px', background: 'white' }} data-testid="highlights-section">
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '64px' }}>
        <p style={{ marginBottom: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#B8860B' }}>
          Featured Items
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 400, color: '#1a1a1a' }}>
          Collection Highlights
        </h2>
        <p style={{ maxWidth: '600px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.7, color: '#666' }}>
          Selected pieces representing the depth, rarity, and historical importance of the archive.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '32px' }}>
        {[
          {
            image: IMAGES.heroCollection,
            title: 'Complete Ticket Archive',
            subtitle: 'Over 50 Original Tickets',
            desc: 'The most comprehensive known collection of original Aloha from Hawaii concert tickets, spanning multiple price points ($10.00, $7.50, $5.00), seating sections (Floor, Arena, Balcony), and including both used stubs and complete unused tickets.'
          },
          {
            image: IMAGES.globePortrait,
            title: 'Globe Tickets Portrait Proof',
            subtitle: 'Production Material',
            desc: 'Original production proof from Globe Tickets featuring the iconic portrait design. This proof documents the printing process and represents a one-of-a-kind production artifact from the ticket creation.'
          },
          {
            image: IMAGES.artworkProofs,
            title: 'Progressive Printing Proofs',
            subtitle: 'Color Separation Archive',
            desc: 'Complete set of progressive color proofs showing each stage of the printing process. These materials document how the final ticket design was achieved through precise color registration and ink layering.'
          },
          {
            image: IMAGES.documents,
            title: 'Production Documents',
            subtitle: 'Correspondence & Contracts',
            desc: 'Original correspondence, contracts, and planning documents from the concert production. These materials provide contemporaneous documentation of the event\'s organization and execution.'
          }
        ].map((item, idx) => (
          <div key={idx} style={{ background: '#fafafa', border: '1px solid #e8e8e8' }}>
            <div style={{ position: 'relative', paddingTop: '66%', overflow: 'hidden', background: '#e8e8e8' }}>
              <img src={item.image} alt={item.title} 
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '28px' }}>
              <p style={{ margin: '0 0 4px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#B8860B' }}>
                {item.subtitle}
              </p>
              <h3 style={{ margin: '0 0 12px', fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', fontWeight: 500, color: '#1a1a1a' }}>
                {item.title}
              </h3>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#666' }}>
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================================
   PROVENANCE & DOCUMENTATION
   ============================================================================ */
const ProvenanceSection = () => (
  <section id="provenance" style={{ padding: '120px 32px', background: '#f5f5f5' }} data-testid="provenance-section">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '64px' }}>
        <p style={{ marginBottom: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#B8860B' }}>
          Provenance & Authentication
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 400, color: '#1a1a1a' }}>
          Documented History & Research
        </h2>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '64px' }}>
        {[
          {
            title: 'Graceland Documentation',
            desc: 'The collection has been documented at Graceland with photographic evidence establishing its presence at the most significant Elvis Presley archive location.'
          },
          {
            title: 'Original Acquisition Records',
            desc: 'Complete acquisition history tracing materials from original sources including direct acquisition from Globe Tickets production archives and period collectors.'
          },
          {
            title: 'Scholarly Research',
            desc: 'Extensive research using contemporaneous 1972-1973 newspaper sources, broadcast records, and primary documentation to verify historical claims.'
          },
          {
            title: 'Evidence-Based Methodology',
            desc: 'Each claim in the archive is classified by source evidence tier. Open questions are acknowledged rather than filled with speculation.'
          }
        ].map((item, idx) => (
          <div key={idx} style={{ padding: '28px', background: 'white', border: '1px solid #e0e0e0' }}>
            <h3 style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>
              {item.title}
            </h3>
            <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#666' }}>
              {item.desc}
            </p>
          </div>
        ))}
      </div>

      <div style={{ padding: '40px', background: 'white', border: '1px solid #e0e0e0', textAlign: 'center' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', fontWeight: 400, color: '#1a1a1a' }}>
          Complete Documentation Package
        </h3>
        <p style={{ maxWidth: '600px', margin: '0 auto', fontFamily: "'Source Sans 3', sans-serif", fontSize: '15px', lineHeight: 1.7, color: '#666' }}>
          Serious inquiries will receive access to the complete research archive including source citations, 
          evidence matrices, timeline documentation, newspaper records, and detailed provenance files for 
          each significant item in the collection.
        </p>
      </div>
    </div>
  </section>
);

/* ============================================================================
   RARITY & SIGNIFICANCE
   ============================================================================ */
const SignificanceSection = () => (
  <section id="significance" style={{ padding: '120px 32px', background: 'white' }} data-testid="significance-section">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '64px' }}>
        <p style={{ marginBottom: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#B8860B' }}>
          Investment Significance
        </p>
        <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 400, color: '#1a1a1a' }}>
          Why This Collection Matters
        </h2>
      </div>

      <div style={{ marginBottom: '64px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px' }}>
          {[
            { label: 'Impossible to Recreate', desc: 'Production materials and proofs no longer exist elsewhere. This archive cannot be reassembled.' },
            { label: 'Complete Archive', desc: 'Not scattered individual items, but an interconnected historical record with documented relationships.' },
            { label: 'First Satellite Concert', desc: 'Documents a genuine technological and entertainment milestone in broadcast history.' },
            { label: 'Elvis at His Peak', desc: 'Captures one of Elvis Presley\'s most celebrated and widely viewed performances.' }
          ].map((item, idx) => (
            <div key={idx} style={{ padding: '24px', background: '#fafafa', borderLeft: '3px solid #B8860B' }}>
              <h4 style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
                {item.label}
              </h4>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.6, color: '#666' }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: '48px', background: '#0a0a0a', color: 'white' }}>
        <h3 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 400, textAlign: 'center' }}>
          Potential for the Acquiring Party
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '32px' }}>
          {[
            { title: 'Museum Exhibition', desc: 'Ready for institutional display with complete documentation and professional presentation materials.' },
            { title: 'Publishing Rights', desc: 'Extensive photography and documentation suitable for book publication, documentary use, or editorial licensing.' },
            { title: 'Long-Term Value', desc: 'Elvis Presley memorabilia continues to appreciate, particularly documented items with clear provenance.' },
            { title: 'Historical Preservation', desc: 'Opportunity to become the custodian of a significant piece of entertainment and broadcast history.' }
          ].map((item, idx) => (
            <div key={idx}>
              <h4 style={{ margin: '0 0 8px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#C9A962' }}>
                {item.title}
              </h4>
              <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.6, color: 'rgba(255,255,255,0.7)' }}>
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================================
   INQUIRY / CALL TO ACTION
   ============================================================================ */
const InquirySection = () => (
  <section id="inquiry" style={{ padding: '120px 32px', background: '#f5f5f5' }} data-testid="inquiry-section">
    <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
      <p style={{ marginBottom: '16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#B8860B' }}>
        Acquisition Inquiry
      </p>
      <h2 style={{ margin: '0 0 24px', fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 400, color: '#1a1a1a' }}>
        This Collection Is Being Prepared for Sale
      </h2>
      <p style={{ marginBottom: '40px', fontFamily: "'Source Serif 4', serif", fontSize: '17px', lineHeight: 1.8, color: '#555' }}>
        The complete Elvis Aloha Archive will be offered for acquisition as a single collection. 
        We are currently accepting inquiries from qualified buyers including private collectors, 
        museums, institutions, and investment groups interested in acquiring this historically 
        significant archive.
      </p>

      <div style={{ padding: '40px', background: 'white', border: '1px solid #e0e0e0', marginBottom: '32px' }}>
        <h3 style={{ margin: '0 0 16px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>
          To Register Your Interest
        </h3>
        <p style={{ margin: '0 0 24px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '14px', lineHeight: 1.7, color: '#666' }}>
          Qualified buyers may request access to the complete documentation package, including 
          detailed inventory, provenance files, high-resolution photography, and research materials.
        </p>
        <a href="mailto:inquiry@elvisalohaarchive.com" style={{
          display: 'inline-block',
          padding: '16px 40px',
          background: '#1a1a1a',
          color: 'white',
          fontFamily: "'Source Sans 3', sans-serif",
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          textDecoration: 'none',
          transition: 'background 0.3s'
        }}>
          Contact for Information
        </a>
      </div>

      <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: '#888' }}>
        All inquiries are confidential. Proof of financial qualification may be required 
        for access to detailed documentation.
      </p>
    </div>
  </section>
);

/* ============================================================================
   PREMIUM FOOTER
   ============================================================================ */
const PremiumFooter = () => (
  <footer style={{ padding: '64px 32px 40px', background: '#0a0a0a', color: 'white' }}>
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '48px', marginBottom: '48px' }}>
        <div style={{ maxWidth: '300px' }}>
          <p style={{ margin: '0 0 16px', fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 600, letterSpacing: '0.05em' }}>
            ELVIS ALOHA ARCHIVE
          </p>
          <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', lineHeight: 1.7, color: 'rgba(255,255,255,0.6)' }}>
            The definitive collection documenting Elvis Presley's historic 
            1973 Aloha from Hawaii satellite broadcast.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '48px', flexWrap: 'wrap' }}>
          <div>
            <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C9A962' }}>
              Navigate
            </p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {['Overview', 'Highlights', 'Provenance', 'Significance'].map(item => (
                <button key={item} onClick={() => scrollToSection(item.toLowerCase())}
                  style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                  {item}
                </button>
              ))}
            </nav>
          </div>
          <div>
            <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#C9A962' }}>
              Legal
            </p>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <a href="/rights" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Rights</a>
              <a href="/privacy" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Privacy</a>
              <a href="/accessibility" style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: '13px', color: 'rgba(255,255,255,0.6)', textDecoration: 'none' }}>Accessibility</a>
            </nav>
          </div>
        </div>
      </div>

      <div style={{ paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <p style={{ margin: '0 0 12px', fontFamily: "'Source Sans 3', sans-serif", fontSize: '11px', lineHeight: 1.7, color: 'rgba(255,255,255,0.4)' }}>
          <strong style={{ color: 'rgba(255,255,255,0.6)' }}>Independent Archive:</strong> Elvis Aloha Archive 
          is an independent historical archive. Not affiliated with Elvis Presley Enterprises, Graceland, 
          or any official Elvis Presley organization. Names and trademarks remain the property of their 
          respective rights holders.
        </p>
        <p style={{ margin: 0, fontFamily: "'Source Sans 3', sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.3)' }}>
          © {new Date().getFullYear()} Elvis Aloha Archive
        </p>
      </div>
    </div>
  </footer>
);

/* ============================================================================
   MAIN AUCTION LANDING PAGE
   ============================================================================ */
const AuctionLandingPage = () => {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Internal Preview - Not for Public</title>
      </Helmet>
      <div data-testid="auction-landing-page" style={{ background: 'white' }}>
        <PremiumNav />
        <HeroSection />
        <OverviewSection />
        <HistoricalSection />
        <HighlightsSection />
        <ProvenanceSection />
        <SignificanceSection />
        <InquirySection />
        <PremiumFooter />
      </div>
    </>
  );
};

export default AuctionLandingPage;
